import { cx } from '@linaria/core';
import { AnimatePresence } from 'framer-motion';
import type {
	CSSProperties,
	MouseEventHandler,
	PropsWithChildren,
} from 'react';
import { Suspense, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import type { TModalProps } from '../types';
import {
	backdropStyle,
	BaseVisibilityWrapper,
	overlayStyle,
} from './Base.styles';

type TProps = PropsWithChildren &
	TModalProps & {
		className?: string;
		style?: CSSProperties;
		withBackdrop?: boolean;
	};

const Base = ({
	locked,
	onClose,
	className,
	style,
	children,
	withBackdrop = true,
	'data-name': dataName,
}: TProps) => {
	const modalRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const modals = document.querySelectorAll('.modal').length;
		if (modals > 1) {
			return;
		}
		const scrollY = window.scrollY;
		const body = document.body;
		body.style.position = 'fixed';
		body.style.top = `-${scrollY}px`;
		body.style.width = '100%';

		modalRef.current?.scrollIntoView({ behavior: 'instant' });
		return () => {
			body.style.position = '';
			body.style.top = '';
			body.style.width = '';
			window.scrollTo(0, scrollY);
		};
	}, []);

	const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
		if (onClose && !locked && e.target === e.currentTarget) {
			onClose();
		}
	};

	return createPortal(
		<BaseVisibilityWrapper
			tabIndex={0}
			ref={modalRef}
			className={cx(
				overlayStyle,
				withBackdrop && backdropStyle,
				'modal',
				className
			)}
			onClick={handleClick}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			style={style}
			autoFocus
			role="dialog"
			data-name={dataName}
		>
			{children}
		</BaseVisibilityWrapper>,
		document.body
	);
};

export const Modal = ({ open, className, style, ...rest }: TProps) => {
	return (
		<AnimatePresence mode="wait" initial={false}>
			<Suspense>
				{open && (
					<Base {...rest} open={open} className={className} style={style} />
				)}
			</Suspense>
		</AnimatePresence>
	);
};
